/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/midnight.tsx
   All Midnight domain cards with text pre-rendered as JSX.
   Edit `rendered` to update display. Edit `text` to update search.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  HOPE,
  STRESS,
  SPELLCAST_ROLL,
  VERY_CLOSE,
  CLOSE,
  FAR,
  VERY_FAR,
  MELEE,
  ADVANTAGE,
  DISADVANTAGE,
  VULNERABLE,
  LOADOUT,
  EVASION,
} from '../../templates';

const midnightCards: DomainCard[] = [
  {
    name: "Pick and Pull",
    level: 1,
    domain: "Midnight",
    type: "Ability",
    recallCost: 0,
    text: "You have advantage on action rolls to pick nonmagical locks, disarm nonmagical traps, or steal items from a target (either through stealth or by force).",
    rendered: (
      <>
        <p key={0} className="card-text-para">You have {ADVANTAGE} on action rolls to pick nonmagical locks, disarm nonmagical traps, or steal items from a target (either through stealth or by force).</p>
      </>
    ),
  },
  {
    name: "Rain of Blades",
    level: 1,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "Spend a Hope to make a Spellcast Roll and conjure throwing blades that strike out at all targets within Very Close range. Targets you succeed against take d8+2 magic damage using your Proficiency.\n\nIf a target you hit is Vulnerable, they take an extra 1d8 damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to make a {SPELLCAST_ROLL} and conjure throwing blades that strike out at all targets within {VERY_CLOSE} range. Targets you succeed against take <strong className="inline-strong">d8+2</strong> magic damage using your Proficiency.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">If a target you hit is {VULNERABLE}, they take an extra <strong className="inline-strong">1d8</strong> damage.</p>
      </>
    ),
  },
  {
    name: "Uncanny Disguise",
    level: 1,
    domain: "Midnight",
    type: "Spell",
    recallCost: 0,
    text: "When you have a few minutes to prepare, you can mark a Stress to don the facade of any humanoid you can picture clearly in your mind. While disguised, you have advantage on Presence Rolls to avoid scrutiny.\n\nPlace a number of tokens equal to your Spellcast trait on this card. When you take an action while disguised, spend a token from this card. After the action that spends the last token is resolved, the disguise drops.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you have a few minutes to prepare, you can <strong className="inline-strong">mark a {STRESS}</strong> to don the facade of any humanoid you can picture clearly in your mind. While disguised, you have {ADVANTAGE} on Presence Rolls to avoid scrutiny.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Place a number of tokens equal to your Spellcast trait on this card. When you take an action while disguised, spend a token from this card. After the action that spends the last token is resolved, the disguise drops.</p>
      </>
    ),
  },
  {
    name: "Midnight Spirit",
    level: 2,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "Spend a Hope to summon a humanoid-sized spirit that can move or carry things for you until your next rest.\n\nYou can also send it to attack an adversary. When you do, make a Spellcast Roll against a target within Very Far range. On a success, the spirit moves into Melee range with that target. Roll a number of d6s equal to your Spellcast trait and deal that much magic damage to the target. The spirit then dissipates. You can only have one spirit at a time.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to summon a humanoid-sized spirit that can move or carry things for you until your next rest.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can also send it to attack an adversary. When you do, make a {SPELLCAST_ROLL} against a target within {VERY_FAR} range. On a success, the spirit moves into {MELEE} range with that target. Roll a number of <strong className="inline-strong">d6s</strong> equal to your Spellcast trait and deal that much magic damage to the target. The spirit then dissipates. You can only have one spirit at a time.</p>
      </>
    ),
  },
  {
    name: "Shadowbind",
    level: 2,
    domain: "Midnight",
    type: "Spell",
    recallCost: 0,
    text: "Make a Spellcast Roll against all adversaries within Very Close range. Targets you succeed against are temporarily Restrained as their shadow binds them in place.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against all adversaries within {VERY_CLOSE} range. Targets you succeed against are temporarily <em>Restrained</em> as their shadow binds them in place.</p>
      </>
    ),
  },
  {
    name: "Chokehold",
    level: 3,
    domain: "Midnight",
    type: "Ability",
    recallCost: 1,
    text: "When you position yourself behind a creature who's about your size, you can mark a Stress to pull them into a chokehold, making them temporarily Vulnerable.\n\nWhen a creature attacks a target who is Vulnerable in this way, they deal an extra 2d6 damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you position yourself behind a creature who's about your size, you can <strong className="inline-strong">mark a {STRESS}</strong> to pull them into a chokehold, making them temporarily {VULNERABLE}.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When a creature attacks a target who is {VULNERABLE} in this way, they deal an extra <strong className="inline-strong">2d6</strong> damage.</p>
      </>
    ),
  },
  {
    name: "Veil of Night",
    level: 3,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll (13). On a success, you can create a temporary curtain of darkness between two points within Far range. Only you can see through this darkness. You're considered Hidden to adversaries on the other side of the veil, and you have advantage on attacks you make through the darkness. The veil remains until you cast another spell.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (13). On a success, you can create a temporary curtain of darkness between two points within {FAR} range. Only you can see through this darkness. You're considered <em>Hidden</em> to adversaries on the other side of the veil, and you have {ADVANTAGE} on attacks you make through the darkness. The veil remains until you cast another spell.</p>
      </>
    ),
  },
  {
    name: "Stealth Expertise",
    level: 4,
    domain: "Midnight",
    type: "Ability",
    recallCost: 0,
    text: "When you roll with Fear while attempting to move unnoticed through a dangerous area, you can mark a Stress to roll with Hope instead.\n\nIf an ally within Close range is also attempting to move unnoticed and rolls with Fear, you can mark a Stress to change their result to a roll with Hope.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you roll with Fear while attempting to move unnoticed through a dangerous area, you can <strong className="inline-strong">mark a {STRESS}</strong> to roll with {HOPE} instead.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">If an ally within {CLOSE} range is also attempting to move unnoticed and rolls with Fear, you can <strong className="inline-strong">mark a {STRESS}</strong> to change their result to a roll with {HOPE}.</p>
      </>
    ),
  },
  {
    name: "Glyph of Nightfall",
    level: 4,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Very Close range. On a success, spend a Hope to conjure a dark glyph upon their body that exposes their weak points, temporarily reducing the target's Difficulty by a value equal to your Knowledge (minimum 1).",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {VERY_CLOSE} range. On a success, <strong className="inline-strong">spend a {HOPE}</strong> to conjure a dark glyph upon their body that exposes their weak points, temporarily reducing the target's Difficulty by a value equal to your Knowledge (minimum 1).</p>
      </>
    ),
  },

  {
    name: "Hush",
    level: 5,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "Make a Spellcast Roll against a target within Close range. On a success, spend a Hope to conjure suppressive magic around the target that encompasses everything within Very Close range of them and follows them as they move.\n\nThe target and anything within the area is Silenced until the GM spends a Fear on their turn to clear this condition, you cast Hush again, or you take Major damage. While Silenced, they can't make noise and can't cast spells.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {CLOSE} range. On a success, <strong className="inline-strong">spend a {HOPE}</strong> to conjure suppressive magic around the target that encompasses everything within {VERY_CLOSE} range of them and follows them as they move.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">The target and anything within the area is <em>Silenced</em> until the GM spends a Fear on their turn to clear this condition, you cast Hush again, or you take Major damage. While <em>Silenced</em>, they can't make noise and can't cast spells.</p>
      </>
    ),
  },
  {
    name: "Phantom Retreat",
    level: 5,
    domain: "Midnight",
    type: "Spell",
    recallCost: 2,
    text: "Spend a Hope to activate Phantom Retreat where you're currently standing. Spend another Hope at any time before your next rest to disappear from where you are and reappear where you were standing when you activated Phantom Retreat. This spell ends after you reappear.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to activate Phantom Retreat where you're currently standing. <strong className="inline-strong">Spend another {HOPE}</strong> at any time before your next rest to disappear from where you are and reappear where you were standing when you activated Phantom Retreat. This spell ends after you reappear.</p>
      </>
    ),
  },
  {
    name: "Dark Whispers",
    level: 6,
    domain: "Midnight",
    type: "Spell",
    recallCost: 0,
    text: "You can speak into the mind of any person with whom you've made physical contact. Once you've opened a channel with them, they can speak back into your mind. Additionally, you can mark a Stress to make a Spellcast Roll against them. On a success, you can ask the GM one of the following questions and receive an answer:\n\n- Where are they?\n- What are they doing?\n- What are they afraid of?\n- What do they cherish most in the world?",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can speak into the mind of any person with whom you've made physical contact. Once you've opened a channel with them, they can speak back into your mind. Additionally, you can <strong className="inline-strong">mark a {STRESS}</strong> to make a {SPELLCAST_ROLL} against them. On a success, you can ask the GM one of the following questions and receive an answer:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Where are they?</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>What are they doing?</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>What are they afraid of?</span></div>
        <div key={5} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>What do they cherish most in the world?</span></div>
      </>
    ),
  },
  {
    name: "Mass Disguise",
    level: 6,
    domain: "Midnight",
    type: "Spell",
    recallCost: 0,
    text: "When you have a few minutes of silence to focus, you can mark a Stress to change the appearance of all willing creatures within Close range. Their new forms must share a general body structure and size, and can be somebody or something you've seen before or entirely fabricated. A disguised creature has advantage on Presence Rolls to avoid scrutiny.\n\nActivate a Countdown (8). It ticks down as a consequence the GM chooses. When it triggers, the disguise drops.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you have a few minutes of silence to focus, you can <strong className="inline-strong">mark a {STRESS}</strong> to change the appearance of all willing creatures within {CLOSE} range. Their new forms must share a general body structure and size, and can be somebody or something you've seen before or entirely fabricated. A disguised creature has {ADVANTAGE} on Presence Rolls to avoid scrutiny.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Activate a Countdown (8). It ticks down as a consequence the GM chooses. When it triggers, the disguise drops.</p>
      </>
    ),
  },
  {
    name: "Midnight-Touched",
    level: 7,
    domain: "Midnight",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Midnight domain, gain the following benefits:\n\n- Once per rest, when you have 0 Hope and the GM would gain a Fear, you can gain a Hope instead.\n- When you make a successful attack, you can mark a Stress to add the result of your Fear Die to your damage roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your {LOADOUT} are from the Midnight domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Once per rest, when you have 0 {HOPE} and the GM would gain a Fear, you can gain a {HOPE} instead.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>When you make a successful attack, you can <strong className="inline-strong">mark a {STRESS}</strong> to add the result of your Fear Die to your damage roll.</span></div>
      </>
    ),
  },
  {
    name: "Vanishing Dodge",
    level: 7,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "When an attack made against you that would deal physical damage fails, you can spend a Hope to envelop yourself in shadow, becoming Hidden and teleporting to a point within Close range of the attacker. You remain Hidden until the next time you make an action roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an attack made against you that would deal physical damage fails, you can <strong className="inline-strong">spend a {HOPE}</strong> to envelop yourself in shadow, becoming <em>Hidden</em> and teleporting to a point within {CLOSE} range of the attacker. You remain <em>Hidden</em> until the next time you make an action roll.</p>
      </>
    ),
  },
  {
    name: "Shadowhunter",
    level: 8,
    domain: "Midnight",
    type: "Ability",
    recallCost: 2,
    text: "Your prowess is enhanced under the cover of shadow. While you're shrouded in low light or darkness, you gain a +1 bonus to your Evasion and make attack rolls with advantage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Your prowess is enhanced under the cover of shadow. While you're shrouded in low light or darkness, you gain a +1 bonus to your {EVASION} and make attack rolls with {ADVANTAGE}.</p>
      </>
    ),
  },
  {
    name: "Spellcharge",
    level: 8,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "When you take magic damage, place tokens equal to the number of Hit Points you marked on this card. You can store a number of tokens equal to your Spellcast trait.\n\nWhen you make a successful attack against a target, you can spend any number of tokens to add a d6 for each token spent to your damage roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you take magic damage, place tokens equal to the number of Hit Points you marked on this card. You can store a number of tokens equal to your Spellcast trait.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you make a successful attack against a target, you can spend any number of tokens to add a <strong className="inline-strong">d6</strong> for each token spent to your damage roll.</p>
      </>
    ),
  },
  {
    name: "Night Terror",
    level: 9,
    domain: "Midnight",
    type: "Spell",
    recallCost: 2,
    text: "Once per long rest, choose any targets within Very Close range to perceive you as a nightmarish horror. The targets must succeed on a Reaction Roll (16) or become temporarily Horrified. While Horrified, they're Vulnerable. Steal a number of Fear from the GM equal to the number of targets that are Horrified (up to the number of Fear in the GM's pool). Roll a number of d6s equal to the number of stolen Fear and deal the total damage to each Horrified target. Discard the stolen Fear.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per long rest, choose any targets within {VERY_CLOSE} range to perceive you as a nightmarish horror. The targets must succeed on a Reaction Roll (16) or become temporarily <em>Horrified</em>. While <em>Horrified</em>, they're {VULNERABLE}. Steal a number of Fear from the GM equal to the number of targets that are <em>Horrified</em> (up to the number of Fear in the GM's pool). Roll a number of <strong className="inline-strong">d6s</strong> equal to the number of stolen Fear and deal the total damage to each <em>Horrified</em> target. Discard the stolen Fear.</p>
      </>
    ),
  },
  {
    name: "Twilight Toll",
    level: 9,
    domain: "Midnight",
    type: "Ability",
    recallCost: 1,
    text: "Choose a target within Far range. When you succeed on an action roll against them that doesn't result in making a damage roll, place a token on this card. When you deal damage to this target, spend any number of tokens to add a d12 for each token spent to your damage roll. You can only hold Twilight Toll on one creature at a time.\n\nWhen you choose a new target or take a rest, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Choose a target within {FAR} range. When you succeed on an action roll against them that doesn't result in making a damage roll, place a token on this card. When you deal damage to this target, spend any number of tokens to add a <strong className="inline-strong">d12</strong> for each token spent to your damage roll. You can only hold Twilight Toll on one creature at a time.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">When you choose a new target or take a rest, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Eclipse",
    level: 10,
    domain: "Midnight",
    type: "Spell",
    recallCost: 2,
    text: "Make a Spellcast Roll (16). Once per long rest on a success, plunge the entire area within Far range into complete darkness only you and your allies can see through. Attack rolls have disadvantage when targeting you or an ally within this shadow.\n\nAdditionally, when you or an ally succeeds with Hope against an adversary within this shadow, the target must mark a Stress.\n\nThis spell lasts until the GM spends a Fear on their turn to clear this effect or you take Severe damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (16). Once per long rest on a success, plunge the entire area within {FAR} range into complete darkness only you and your allies can see through. Attack rolls have {DISADVANTAGE} when targeting you or an ally within this shadow.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Additionally, when you or an ally succeeds with {HOPE} against an adversary within this shadow, the target must mark a {STRESS}.</p>
        <div key={3} className="card-text-gap" />
        <p key={4} className="card-text-para">This spell lasts until the GM spends a Fear on their turn to clear this effect or you take Severe damage.</p>
      </>
    ),
  },
  {
    name: "Specter of the Dark",
    level: 10,
    domain: "Midnight",
    type: "Spell",
    recallCost: 1,
    text: "Mark a Stress to become Spectral until you make an action roll targeting another creature. While Spectral, you're immune to physical damage and can float and pass through solid objects. Other creatures can still see you while you're in this form.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Mark a {STRESS}</strong> to become <em>Spectral</em> until you make an action roll targeting another creature. While <em>Spectral</em>, you're immune to physical damage and can float and pass through solid objects. Other creatures can still see you while you're in this form.</p>
      </>
    ),
  },
];

export default midnightCards;
